import { useMutation, useQueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { exportKeys } from '../export/entity';
import type { HookMutationConfig } from '../shared/react-query-types';
import type { AttributeScope } from './entity';

/**
 * CSV exports of the data index.
 *
 * Each call queues a background job and answers with no content; the file
 * arrives in the workspace's exports list rather than in the response. There
 * are two exports per family — the rows themselves, and the same rows with
 * their usage expanded — because the usage export is expensive enough that
 * the backend keeps it separate.
 */

/** Which export to queue: the rows, or the rows with their usage. */
export type DataIndexExportContent = 'rows' | 'usage';

/**
 * What to export. The workspace is a separate argument because narrowing a
 * union survives being passed along, but not being spread into a new object.
 */
export type DataIndexExportTarget = { content: DataIndexExportContent } & (
  | { family: 'events' }
  | { family: 'attributes'; scope: AttributeScope }
);

function environmentPath(workspaceId: number) {
  return { environment_id: String(workspaceId) };
}

/** @public */
export async function queueDataIndexExport(workspaceId: number, target: DataIndexExportTarget): Promise<void> {
  const path = environmentPath(workspaceId);
  const wantsUsage = target.content === 'usage';

  if (target.family === 'events') {
    await (wantsUsage
      ? hydraUiApi.dataIndex.eventsUsage({ params: { path } })
      : hydraUiApi.dataIndex.events({ params: { path } }));
    return;
  }

  const { scope } = target;

  switch (scope.kind) {
    case 'profile':
      await (wantsUsage
        ? hydraUiApi.dataIndex.attributesUsage({ params: { path } })
        : hydraUiApi.dataIndex.attributes({ params: { path } }));
      return;
    case 'object': {
      const params = { path: { ...path, type_id: String(scope.objectTypeId) } };
      await (wantsUsage
        ? hydraUiApi.dataIndex.objectAttributesUsage({ params })
        : hydraUiApi.dataIndex.objectAttributes({ params }));
      return;
    }
    case 'relationship': {
      const params = { path: { ...path, type_id: String(scope.objectTypeId) } };
      await (wantsUsage
        ? hydraUiApi.dataIndex.relationshipAttributesUsage({ params })
        : hydraUiApi.dataIndex.relationshipAttributes({ params }));
      return;
    }
    default: {
      const _exhaustive: never = scope;
      throw new Error(`unhandled attribute scope: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

type ExportOptions = {
  workspaceId: number;
  mutationConfig?: HookMutationConfig<void, DataIndexExportTarget>;
};

/** @public */
export const useQueueDataIndexExport = ({ workspaceId, mutationConfig }: ExportOptions) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (target: DataIndexExportTarget) => queueDataIndexExport(workspaceId, target),
    onSuccess: (result, ...args) => {
      // The queued job shows up as a row in the exports list, which is cached.
      void queryClient.invalidateQueries({ queryKey: exportKeys.lists(workspaceId) });
      onSuccess?.(result, ...args);
    },
    ...rest,
  });
};
